import { useCallback, useEffect, useMemo, useState } from "react";
import { getRuleLogs } from "../../API/ruleLogsApi";

export const looksLikeRow = (value) =>
  value && typeof value === "object" && !Array.isArray(value) && (
    "ruleName" in value ||
    "valueText" in value ||
    "changedBy" in value ||
    "changedDate" in value ||
    "id" in value
  );

// Normalize varying API payload shapes to a flat array.
export const extractRows = (payload, seen = new Set()) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  if (seen.has(payload)) {
    return [];
  }

  seen.add(payload);

  const candidates = ["queryResults", "elements", "data", "rows", "content", "results", "items", "logs"];
  for (const key of candidates) {
    const value = payload[key];
    if (Array.isArray(value)) {
      return value;
    }
    const nestedRows = extractRows(value, seen);
    if (nestedRows.length) {
      return nestedRows;
    }
  }

  if (looksLikeRow(payload)) {
    return [payload];
  }

  for (const value of Object.values(payload)) {
    const nestedRows = extractRows(value, seen);
    if (nestedRows.length) {
      return nestedRows;
    }
  }

  return [];
};

// Ensure every row has an id for DataGrid.
export const ensureRowIds = (rows, ruleType) =>
  rows.map((row, index) => {
    const fallbackId = `${ruleType || "rule"}-${row?.ruleName || row?.item || row?.category || "row"}-${index}`;
    return {
      id: row?.id ?? row?.logId ?? row?.ruleLogId ?? row?.ruleId ?? row?.recordId ?? fallbackId,
      ...row,
    };
  });

export const useRuleLogs = (ruleType, refreshKey, { enabled = true, initialRows = [] } = {}) => {
  const [rawRows, setRawRows] = useState(initialRows);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) {
      setRawRows(initialRows);
    }
  }, [enabled, initialRows]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const logs = await getRuleLogs(ruleType);
      setRawRows(logs || []);
    } finally {
      setLoading(false);
    }
  }, [ruleType]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    fetchLogs();
  }, [enabled, fetchLogs, refreshKey]);

  const rows = useMemo(() => ensureRowIds(extractRows(rawRows), ruleType), [rawRows, ruleType]);

  return { rows, loading, fetchLogs };
};

export default useRuleLogs;
